/** @jsx createElement */

import type {
  FeatureServiceBinder,
  FeatureServiceProviderDefinition,
  SharedFeatureService,
} from '@feature-hub/core';
import {createElement} from '@snugjs/html';
import type {
  AdbContextCookieV2,
  AdbContextV2,
} from './parse-adb-context-cookie.js';
import {parseAdbContextCookie} from './parse-adb-context-cookie.js';

export interface AdbContextServiceV1 {
  readonly isAdbContextSet: boolean;
  readonly resetAdbContext: () => void;
}

export interface AdbContextServiceV2 {
  readonly adbContext: AdbContextV2 | undefined;
  readonly resetAdbContext: () => void;
}

export type {AdbContextV2, AdbContextCookieV2};

export interface SharedAdbContextService extends SharedFeatureService {
  readonly '1.0.0': FeatureServiceBinder<AdbContextServiceV1>;
  readonly '2.0.0': FeatureServiceBinder<AdbContextServiceV2>;
}

export interface AdbContextServiceOptions {
  readonly cookieName: string;
  readonly resetPathname: string;
}

export function defineAdbContextService(
  options: AdbContextServiceOptions,
): FeatureServiceProviderDefinition<SharedAdbContextService> {
  const {cookieName, resetPathname} = options;

  return {
    id: `adb-context-service`,

    create: () => {
      const adbContextCookie = parseAdbContextCookie(cookieName);

      // https://web.dev/bfcache/#update-stale-or-sensitive-data-after-bfcache-restore
      window.addEventListener(`pageshow`, (event) => {
        if (
          event.persisted &&
          adbContextCookie?.key !== parseAdbContextCookie(cookieName)?.key
        ) {
          location.reload();
        }
      });

      const resetAdbContext = () => {
        const {pathname, search, hash} = document.location;

        const formElement = (
          <form action={resetPathname} method="POST">
            <input
              name="redirectPath"
              type="hidden"
              value={`${pathname}${search}${hash}`}
            />
          </form>
        ) as HTMLFormElement;

        document.body.appendChild(formElement);

        formElement.submit();
        formElement.remove();
      };

      return {
        '1.0.0': () => {
          return {
            featureService: {
              isAdbContextSet: Boolean(adbContextCookie),
              resetAdbContext,
            },
          };
        },
        '2.0.0': () => {
          return {
            featureService: {
              adbContext: adbContextCookie?.adbContext,
              resetAdbContext,
            },
          };
        },
      };
    },
  };
}
